import React from 'react';
import Done from '@mui/icons-material/Done';
import Close from '@mui/icons-material/Close';
import Download from '@mui/icons-material/Download';
import { styled, Box, Typography, IconButton } from '@mui/material';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';

import { MuiCardForm } from 'lib/form/MuiCardForm';
import { SettingDisplay } from 'lib/SettingDisplay';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { ImageFormField } from 'lib/form/fields/ImageFormField';
import { NumberFormField } from 'lib/form/fields/NumberFormField';
import { useDownloadAsset } from 'lib/hooks/useDownloadAsset';

const PREFIX = 'MarketingSettings';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`,
    image: `${PREFIX}-image`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2),
        overflowY: 'visible' // Needed for the dropdowns
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(2)
    },
    [`& .${classes.image}`]: {
        width: 'auto',
        height: 'auto',
        maxHeight: '400px',
        maxWidth: '100%'
    }
}));

type IProps = {
    settings: IPublicAndPrivateSettings;
    tenantId: string;
    onSubmit: (values: Record<string, any>) => Promise<boolean>;
};

export function ReferralsSettingsCard({ settings, tenantId, onSubmit }: IProps) {
    const referralBannerImage = `${
        import.meta.env.MEDIA_SERVICE_URL
    }/tenants/${tenantId}/app_assets/referral_banner`;
    const { loading, handleDownload } = useDownloadAsset({
        src: referralBannerImage,
        fileName: 'referral_banner'
    });

    return (
        <StyledMuiCardForm
            title="Referrals"
            initialValues={{
                referralEnabled: settings?.referralEnabled ?? false,
                stampsPerReferer: settings?.referral?.stampsPerReferer,
                stampsPerReferee: settings?.referral?.stampsPerReferee,
                messageTemplate: settings?.referral?.messageTemplate,
                referralBanner: referralBannerImage
            }}
            onSubmit={onSubmit}
            childrenWhileDisplaying={
                <Box display="flex" flexDirection="column">
                    <SettingDisplay label="Referrals Enabled">
                        {settings?.referralEnabled && <Done />}
                        {!settings?.referralEnabled && <Close />}
                    </SettingDisplay>
                    <SettingDisplay label="Stamps Per Referer">
                        <Typography variant="body2">{settings?.referral?.stampsPerReferer}</Typography>
                    </SettingDisplay>
                    <SettingDisplay label="Stamps Per Referee">
                        <Typography variant="body2">{settings?.referral?.stampsPerReferee}</Typography>
                    </SettingDisplay>
                    <SettingDisplay label="Referrals Copy">
                        <Typography variant="body2">{settings?.referral?.messageTemplate}</Typography>
                    </SettingDisplay>
                    <SettingDisplay
                        label="Referrals Banner"
                        description="640px by 445px PNG or JPEG"
                        buildtimeAndroid
                        buildtimeIOS
                    >
                        <Box display="flex" flexDirection="row" flexWrap="wrap" alignItems="center" gap={1}>
                            <img src={referralBannerImage} className={classes.image} />
                            <IconButton disabled={loading} onClick={handleDownload}>
                                <Download />
                            </IconButton>
                        </Box>
                    </SettingDisplay>
                </Box>
            }
            childrenWhileEditing={
                <Box display="flex" flexDirection="column">
                    <SettingDisplay label="Referrals Enabled">
                        <SwitchFormField name="referralEnabled" />
                    </SettingDisplay>
                    <SettingDisplay label="Stamps Per Referer">
                        <NumberFormField name="stampsPerReferer" />
                    </SettingDisplay>
                    <SettingDisplay label="Stamps Per Referee">
                        <NumberFormField name="stampsPerReferee" />
                    </SettingDisplay>
                    <SettingDisplay label="Referrals Copy">
                        <TextFormField name="messageTemplate" />
                    </SettingDisplay>
                    <SettingDisplay
                        label="Referrals Banner"
                        description="640px by 445px PNG or JPEG"
                        buildtimeAndroid
                        buildtimeIOS
                    >
                        <ImageFormField
                            name="referralBanner"
                            aspect={640 / 445}
                            fileName="referral_banner"
                            uploadFolder="app_assets"
                            unremovable
                        />
                    </SettingDisplay>
                </Box>
            }
            className={classes.settingsCard}
            boxClassName={classes.displayClassName}
        />
    );
}
